import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authorizeStream } from '../shared/api/orders'
import { playNewOrderChime, unlockAudio } from './sound'
import type { Order } from '../shared/types'

export const BOARD_KEY = ['board', 'orders']
const TERMINAL = new Set(['DELIVERED', 'CANCELLED'])
const HIGHLIGHT_MS = 4000

/** 'connecting' until the first open, so a normal page load doesn't flash the offline banner. */
export type StreamStatus = 'connecting' | 'live' | 'offline'

/** Applies an order to the board list: terminal orders leave the queue, the rest replace or prepend. */
export function mergeBoardOrder(list: Order[] | undefined, order: Order): Order[] {
  const prev = list ?? []
  if (TERMINAL.has(order.status)) {
    return prev.filter((o) => o.id !== order.id)
  }
  return prev.some((o) => o.id === order.id)
    ? prev.map((o) => (o.id === order.id ? order : o))
    : [order, ...prev]
}

/**
 * Subscribes the live board to the SSE stream (§7). Mints the httpOnly cookie (`POST /stream/auth`),
 * opens an `EventSource`, and merges `order.created` / `order.updated` into the `['board','orders']`
 * cache. The browser auto-reconnects for transient drops (the cookie lives ~30 min — its own
 * lifetime, not the 15-min access token's, D-053); on a closed connection (e.g. expired cookie) we
 * re-mint the cookie and reopen — the mint call rides `ownerApi`, so an expired access token
 * silently refreshes first.
 *
 * <p>The chime is driven by the DATA, not by the transport. SSE has no replay: it sends no event
 * ids and buffers nothing, so an order placed while the phone was in a lift, or while the tab was
 * backgrounded, is simply never delivered — it would appear silently on the next 30s poll and the
 * owner would never know. Instead every order id that reaches the board list is checked against
 * `announced`, whatever route it arrived by (stream, poll, focus-refetch, reconnect). Each order
 * therefore chimes exactly once, and a missed order still announces itself when the list catches up.
 *
 * Takes the board list so it can do that check; returns the ids to highlight as "just arrived"
 * plus the connection status (the board must say when it has gone quiet — see §7's k8s caveat,
 * a dropped stream is otherwise indistinguishable from a quiet shop).
 */
export function useOrderStream(
  enabled: boolean,
  orders: Order[] | undefined,
): { newOrderIds: Set<string>; status: StreamStatus } {
  const queryClient = useQueryClient()
  const [newOrderIds, setNewOrderIds] = useState<Set<string>>(() => new Set())
  const [status, setStatus] = useState<StreamStatus>('connecting')
  const highlightTimers = useRef<Map<string, number>>(new Map())
  /** Orders the owner has already been told about. Seeded from the first list so a fresh sign-in
   *  doesn't chime for the whole existing queue. */
  const announced = useRef<Set<string>>(new Set())
  const seeded = useRef(false)

  const flagNew = useCallback((id: string) => {
    setNewOrderIds((prev) => new Set(prev).add(id))
    const existing = highlightTimers.current.get(id)
    if (existing) {
      window.clearTimeout(existing)
    }
    const t = window.setTimeout(() => {
      setNewOrderIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      highlightTimers.current.delete(id)
    }, HIGHLIGHT_MS)
    highlightTimers.current.set(id, t)
  }, [])

  /** Marks an order as announced and highlights it; false if the owner had already seen it. */
  const markAnnounced = useCallback(
    (id: string) => {
      if (announced.current.has(id)) {
        return false
      }
      announced.current.add(id)
      flagNew(id)
      return true
    },
    [flagNew],
  )

  // Forget the announced set when the session ends, so the next staff member to sign in on this
  // device gets a clean seed instead of inheriting the previous one's.
  useEffect(() => {
    if (!enabled) {
      announced.current = new Set()
      seeded.current = false
      setStatus('connecting')
    }
  }, [enabled])

  // Chime for anything in the list the owner hasn't been told about yet — this is what covers
  // the gaps the stream cannot (see the note above).
  useEffect(() => {
    if (!enabled || !orders) {
      return
    }
    if (!seeded.current) {
      seeded.current = true
      // Orders already announced by an early SSE event stay announced; the rest are pre-existing.
      orders.forEach((o) => announced.current.add(o.id))
      return
    }
    let fresh = false
    for (const o of orders) {
      if (markAnnounced(o.id)) {
        fresh = true
      }
    }
    if (fresh) {
      playNewOrderChime()
    }
  }, [enabled, orders, markAnnounced])

  // Browsers block audio until a user gesture. Sign-in provides one, but the login now survives
  // reloads (D-053) — a refreshed or restored board can reach here having never been clicked, and
  // would then run silently. Unlock on the owner's first touch instead.
  useEffect(() => {
    if (!enabled) {
      return
    }
    const unlock = () => unlockAudio()
    window.addEventListener('pointerdown', unlock, { once: true })
    window.addEventListener('keydown', unlock, { once: true })
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [enabled])

  // Drop pending highlight timers on unmount.
  useEffect(() => {
    const timers = highlightTimers.current
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      timers.clear()
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      return
    }

    let closed = false
    let es: EventSource | null = null
    let reconnectTimer: number | undefined
    let backoff = 1000

    const applyOrder = async (raw: string, announce: boolean) => {
      const order = JSON.parse(raw) as Order
      // Drop any in-flight board refetch first. TanStack Query does NOT discard a fetch that
      // resolves after a manual setQueryData, so a 30s poll snapshotted *before* this event
      // would land after it and revert the card (see cancelQueries in the optimistic-update docs).
      await queryClient.cancelQueries({ queryKey: BOARD_KEY })
      queryClient.setQueryData<Order[]>(BOARD_KEY, (prev) => mergeBoardOrder(prev, order))
      // Chime here too so a stream event that beats the first list load still announces itself;
      // the list effect then sees it as already announced and stays quiet.
      if (announce && markAnnounced(order.id)) {
        playNewOrderChime()
      }
    }

    // One guard for both listeners. `applyOrder` is async, so a malformed payload (JSON.parse)
    // or a rejected cancelQueries would otherwise be an unhandled rejection — the order silently
    // never reaches the board, with nothing in the console to say so.
    const onEvent = (announce: boolean) => (e: MessageEvent) =>
      void applyOrder(e.data, announce).catch((err) => {
        console.error('[order-stream] dropped an event:', err)
      })

    const onCreated = onEvent(true)
    const onUpdated = onEvent(false)

    const scheduleReconnect = () => {
      reconnectTimer = window.setTimeout(() => {
        backoff = Math.min(backoff * 2, 30000)
        void connect()
      }, backoff)
    }

    const connect = async () => {
      try {
        await authorizeStream()
      } catch (err) {
        // The offline banner alone cannot distinguish a 401 from a dead network from a tenant
        // mismatch — all three look like "quiet shop" to the owner and identical in devtools.
        console.error('[order-stream] stream auth failed:', err)
        if (!closed) {
          setStatus('offline')
          scheduleReconnect()
        }
        return
      }
      if (closed) {
        return
      }
      const source = new EventSource('/api/stream/orders')
      es = source
      source.addEventListener('order.created', onCreated as EventListener)
      source.addEventListener('order.updated', onUpdated as EventListener)
      source.onopen = () => {
        backoff = 1000
        setStatus('live')
      }
      source.onerror = () => {
        // CONNECTING (0): the browser is retrying natively. CLOSED (2): re-mint the cookie + reopen.
        // Either way the board is no longer receiving — say so.
        // EventSource errors carry no detail by design; readyState is the only discriminator
        // there is, and it separates "retrying" from "gave up" — worth having in the log.
        console.error('[order-stream] connection error, readyState=%d', source.readyState)
        setStatus('offline')
        if (source.readyState === EventSource.CLOSED && !closed) {
          source.close()
          scheduleReconnect()
        }
      }
    }

    void connect()

    return () => {
      closed = true
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer)
      }
      es?.close()
    }
  }, [enabled, queryClient, markAnnounced])

  return { newOrderIds, status }
}
